import type { ContractCostMap } from '@supply-mover-2-0/core';
import {
	getApplicableContractCost,
	getApplicableContractPrice,
	isPriceFromContract,
} from '@supply-mover-2-0/core';

interface ValidationMessage {
	type:
		| 'perOrderLimit'
		| 'dailyLimit'
		| 'monthlyLimit'
		| 'minimumPrice'
		| 'priceBelowCost'
		| 'outOfStock'
		| 'shortDate'
		| 'licenseExpired'
		| 'licenseInvalid'
		| 'creditHold'
		| 'creditLimit'
		| 'minimumOrderPrice'
		| 'maximumDiscount'
		| 'deaLicenseMissing'
		| 'itemTypeRestriction'
		| 'stateBoardLicenseInvalid'
		| 'deaLicenseInvalid'
		| 'shippingAddressNotFound'
		| 'controlledSubstancePercentage';
	level: 'warning' | 'holdWarning' | 'error';
	limit?: number;
	current?: number;
	todayQuantity?: number;
	monthQuantity?: number;
	message?: string;
	data?: unknown;
	isBlocking?: boolean;
}

interface ValidationState {
	hasError: boolean;
	hasWarning: boolean;
	isBlocking: boolean;
	messages: Array<ValidationMessage>;
	isOptimistic: boolean;
	timestamp: number;
}

interface OrderValidationState {
	hasError: boolean;
	hasWarning: boolean;
	messages: Array<ValidationMessage>;
	timestamp: number;
}

interface CartItem {
	quantity: number;
	price: number;
	isOptimistic?: boolean;
}

interface AddressLicense {
	id: number;
	type: string;
	licenseNumber: string | null;
	expirationDate: string | null;
	isExpired: boolean;
	isExpiringSoon: boolean;
}

/**
 * Helper to check if a license is truly valid.
 * A valid license requires:
 * 1. License object exists
 * 2. License number is set and not empty
 * 3. License is not expired
 */
function isLicenseValid(license: AddressLicense | undefined | null): boolean {
	if (license == null) {
		return false;
	}
	if (license.licenseNumber == null || license.licenseNumber.trim() === '') {
		return false;
	}
	if (license.isExpired) {
		return false;
	}
	return true;
}

/**
 * Words a license-check failure by its actual cause (PD-14937). A license
 * that is simply not on file for the SELECTED shipping address — while the
 * client holds one on another address — is an address mismatch, not an
 * expired license; reporting it as "invalid or expired" sent users chasing
 * renewals when the fix was picking the right shipping address.
 */
function getLicenseFailureMessage(
	kind: 'State Board' | 'DEA',
	license: AddressLicense | undefined,
	existsElsewhere: boolean,
): string {
	if (license == null) {
		return existsElsewhere
			? `Shipping address does not match the ${kind} license address`
			: `No ${kind} license on file for the shipping address`;
	}
	return `${kind} license invalid or expired`;
}

interface AddressControls {
	isRxAllowed: boolean;
	isSchedule2Allowed: boolean;
	isSchedule2NAllowed: boolean;
	isSchedule3Allowed: boolean;
	isSchedule3NAllowed: boolean;
	isSchedule4Allowed: boolean;
	isSchedule5Allowed: boolean;
}

interface ControlledSubstanceCheck {
	enabled: boolean;
	// CRM-specific fields (used by frontend validation)
	crmMaximumControlPercent: number;
	crmUnitType: 'unitsPurchased' | 'dollarAmount' | 'sizeCountQuantity';
	// Legacy fields for backward compatibility
	maximumControlPercent: number;
	unitType: 'unitsPurchased' | 'dollarAmount' | 'sizeCountQuantity';
	crm: 'off' | 'warning' | 'holdWarning' | 'error';
}

interface ContractPriceBreak {
	minimumQuantity: number;
	price: number;
	contractId: number;
	contractName: string;
}

interface ValidationContext {
	itemLimits: Record<number, number>;
	dailyLimits: Record<number, number>;
	dailyQuantities: Record<number, number>;
	monthlyLimits?: Record<number, number>;
	// Quantities ordered over the rolling last-30-days window (monthly limit)
	monthQuantities?: Record<number, number>;
	minimumPrices: Record<number, number>;
	stockLevels: Record<number, number>;
	// Items flagged short date (item.isShortDate). Drives the short-date
	// order-submission check's row warning, like stockLevels drives outOfStock.
	shortDates?: Record<number, boolean>;
	// State-resolved schedule per item (one row, or null when unscheduled for
	// the selected shipping address). Resolution happens server-side.
	effectiveDeaSchedules: Record<
		number,
		{
			licenseLevel: string | null;
			state: string | null;
			schedule: string | null;
		} | null
	>;
	contractPrices: Record<number, number>;
	contractPriceBreaks?: Record<number, Array<ContractPriceBreak>>;
	// Contract-driven cost breaks per item (same map the cart threads into GP).
	// When an item is on a contract cost, the below-cost check is skipped: the
	// contract cost is the authoritative acquisition/sell cost the backend locks
	// in at submit, so a cart price above it is not "below cost" even if it is
	// below the item's own stored cost. Mirrors the contractPriceBreaks bypass in
	// the minimum-price / below-cost checks. (PD-14364)
	contractCosts?: ContractCostMap;
	canOrderOverDailyLimit: boolean;
	canOrderOverDailyItemLimit: boolean;
	checkConfigs: Array<{
		type: string;
		level: 'off' | 'warning' | 'holdWarning' | 'error';
	}>;
	selectedAddressId?: number | null;
	// Client-level validation data
	clientCreditLimit?: number | null;
	clientCreditBalance?: number;
	clientHasCreditHold?: boolean;
	// License data for address
	addressLicenses?: Array<AddressLicense>;
	// Order-level configuration
	minimumOrderPrice?: number;
	maximumDiscount?: { value: number; type: 'percentage' | 'amount' };
	// Address control flags (MedMover-specific)
	addressControls?: AddressControls;
	// True when the cart's selected shipping address no longer resolves to a
	// live record (e.g. it was retired by the NetSuite address sync). When set,
	// the per-item address-control/license checks are skipped — their inputs
	// fail-closed to "nothing allowed / no licenses", which would otherwise
	// surface as a misleading RX/license block — and a single blocking
	// "shipping address not found" order message is shown instead.
	addressNotFound?: boolean;
	// Which license kinds exist on the client's OTHER addresses (from the
	// shop endpoint's licensesElsewhere). Distinguishes "shipping address
	// does not match the licensed address" from "no license on file".
	licensesElsewhere?: { stateBoard: boolean; dea: boolean };
	// Controlled substance percentage check config
	controlledSubstanceCheck?: ControlledSubstanceCheck;
	// Size counts for UOM quantity calculation in controlled substance checks
	itemSizeCounts?: Record<number, number>;
	// Cost data per item for price-below-cost validation
	itemCosts?: Record<
		number,
		{
			sellCost: number | null;
			acquisitionCost: number | null;
			averageCost: number | null;
		}
	>;
	// Price below cost check configuration
	priceBelowCostCheck?: {
		enabled: boolean;
		costField: string;
		crm: 'off' | 'warning' | 'hold' | 'holdWarning' | 'error';
	};
}

type ValidationListener = (
	itemId: number,
	validationState: ValidationState,
) => void;
type OrderValidationListener = (
	orderValidationState: OrderValidationState,
) => void;

/**
 * Centralized service for managing cart validation state across the application.
 * Handles both optimistic and confirmed cart states for immediate user feedback.
 */
class CartValidationManagerClass {
	private readonly listeners: Map<number, Set<ValidationListener>> = new Map();
	private readonly orderListeners: Set<OrderValidationListener> = new Set();
	private readonly validationStates: Map<number, ValidationState> = new Map();
	private orderValidationState: OrderValidationState | null = null;
	private cartItems: Record<number, CartItem> = {};
	private context: ValidationContext | null = null;
	private orderTotals: { subtotal: number; discount: number; total: number } = {
		subtotal: 0,
		discount: 0,
		total: 0,
	};

	/**
	 * Set the validation context (limits, permissions, etc.)
	 */
	setValidationContext(context: ValidationContext): void {
		this.context = context;
		this.revalidateAll();
	}

	/**
	 * Get the effective contract price for an item at a given quantity.
	 * Delegates to shared utility in @supply-mover-2-0/core.
	 */
	getContractPriceForQuantity(itemId: number, quantity: number): number | null {
		return getApplicableContractPrice(
			this.context?.contractPriceBreaks,
			itemId,
			quantity,
		);
	}

	/**
	 * Get all price breaks for an item (for display purposes)
	 */
	getContractPriceBreaks(itemId: number): Array<ContractPriceBreak> | null {
		return this.context?.contractPriceBreaks?.[itemId] ?? null;
	}

	/**
	 * Check if contract prices have been loaded.
	 * Returns true if contractPriceBreaks is not null (even if empty {}).
	 * This distinguishes between "not loaded yet" (null) and "loaded but no contracts" ({}).
	 */
	public areContractPricesLoaded(): boolean {
		return this.context?.contractPriceBreaks != null;
	}

	/**
	 * Update order totals for order-level validation
	 */
	updateOrderTotals(subtotal: number, discount: number, total: number): void {
		this.orderTotals = { subtotal, discount, total };
		this.validateOrder();
	}

	/**
	 * Update cart item data and trigger validation
	 */
	updateCartItem(
		itemId: number,
		cartItem: CartItem | null,
		isOptimistic = false,
	): void {
		if (cartItem === null) {
			delete this.cartItems[itemId];
		} else {
			this.cartItems[itemId] = {
				...cartItem,
				isOptimistic,
			};
		}

		this.validateItem(itemId);
	}

	/**
	 * Update multiple cart items at once
	 */
	updateCartItems(items: Record<number, CartItem>, isOptimistic = false): void {
		// Clear validation for items no longer in cart
		const newItemIds = new Set(
			Object.keys(items).map((id) => parseInt(id, 10)),
		);
		const currentItemIds = Object.keys(this.cartItems).map((id) =>
			parseInt(id, 10),
		);

		currentItemIds.forEach((itemId) => {
			if (!newItemIds.has(itemId)) {
				delete this.cartItems[itemId];
				this.clearValidation(itemId);
			}
		});

		// Update/validate items in the new cart state
		Object.entries(items).forEach(([itemIdStr, cartItem]) => {
			const itemId = parseInt(itemIdStr, 10);
			this.cartItems[itemId] = {
				...cartItem,
				isOptimistic,
			};
			this.validateItem(itemId);
		});
	}

	/**
	 * Subscribe to validation state changes for a specific item
	 */
	subscribe(itemId: number, listener: ValidationListener): () => void {
		if (!this.listeners.has(itemId)) {
			this.listeners.set(itemId, new Set());
		}

		this.listeners.get(itemId)!.add(listener);

		// Immediately call listener with current state
		const currentState = this.validationStates.get(itemId);
		if (currentState) {
			listener(itemId, currentState);
		}

		// Return unsubscribe function
		return () => {
			const itemListeners = this.listeners.get(itemId);
			if (itemListeners) {
				itemListeners.delete(listener);
				if (itemListeners.size === 0) {
					this.listeners.delete(itemId);
				}
			}
		};
	}

	/**
	 * Get current validation state for an item
	 */
	getValidationState(itemId: number): ValidationState | null {
		return this.validationStates.get(itemId) || null;
	}

	/**
	 * Validate a specific item and notify listeners
	 */
	private validateItem(itemId: number): void {
		if (!this.context) {
			return;
		}

		const cartItem = this.cartItems[itemId];
		const cartQuantity = cartItem?.quantity || 0;
		const cartPrice = parseFloat(String(cartItem?.price || 0)) || 0;

		// Skip validation if no cart quantity
		if (cartQuantity <= 0) {
			this.clearValidation(itemId);
			return;
		}

		const messages: ValidationMessage[] = [];
		let hasError = false;
		let hasWarning = false;

		// Get config for validation levels
		const perOrderConfig = this.context.checkConfigs.find(
			(c) => c.type === 'perOrderLimit',
		);
		const dailyConfig = this.context.checkConfigs.find(
			(c) => c.type === 'dailyItemLimit',
		);

		// Check per-order limit violation
		const perOrderLimit = this.context.itemLimits[itemId];
		if (perOrderLimit && cartQuantity > perOrderLimit) {
			if (perOrderConfig && perOrderConfig.level !== 'off') {
				const isWarning =
					perOrderConfig.level === 'warning' ||
					perOrderConfig.level === 'holdWarning';
				messages.push({
					type: 'perOrderLimit',
					level: perOrderConfig.level,
					limit: perOrderLimit,
					current: cartQuantity,
				});

				if (isWarning) {
					hasWarning = true;
				} else {
					hasError = true;
				}
			} else if (!perOrderConfig && !this.context.canOrderOverDailyLimit) {
				messages.push({
					type: 'perOrderLimit',
					level: 'error',
					limit: perOrderLimit,
					current: cartQuantity,
				});
				hasError = true;
			}
		}

		// Check daily limit violation
		const dailyLimit = this.context.dailyLimits[itemId];
		const todayQuantity =
			parseInt(String(this.context.dailyQuantities[itemId] || 0), 10) || 0;
		const numericCartQuantity = parseInt(String(cartQuantity || 0), 10) || 0;
		const totalQuantity = numericCartQuantity + todayQuantity;

		// Debug daily limit validation

		if (dailyLimit && totalQuantity > dailyLimit) {
			if (dailyConfig && dailyConfig.level !== 'off') {
				const isWarning =
					dailyConfig.level === 'warning' ||
					dailyConfig.level === 'holdWarning';
				messages.push({
					type: 'dailyLimit',
					level: dailyConfig.level,
					limit: dailyLimit,
					current: cartQuantity,
					todayQuantity,
				});

				if (isWarning) {
					hasWarning = true;
				} else {
					hasError = true;
				}
			} else if (!dailyConfig && !this.context.canOrderOverDailyItemLimit) {
				messages.push({
					type: 'dailyLimit',
					level: 'error',
					limit: dailyLimit,
					current: cartQuantity,
					todayQuantity,
				});
				hasError = true;
			}
		}

		// Check monthly limit violation (rolling last-30-days window, PD-14038).
		// Unlike daily there is no legacy permission fallback — the check only
		// fires when a monthlyItemLimit preference is configured and not off.
		const monthlyConfig = this.context.checkConfigs.find(
			(c) => c.type === 'monthlyItemLimit',
		);
		const monthlyLimit = this.context.monthlyLimits?.[itemId];
		const monthQuantity =
			parseInt(String(this.context.monthQuantities?.[itemId] ?? 0), 10) || 0;
		const monthTotalQuantity = numericCartQuantity + monthQuantity;

		if (
			monthlyLimit != null &&
			monthlyLimit > 0 &&
			monthTotalQuantity > monthlyLimit &&
			monthlyConfig != null &&
			monthlyConfig.level !== 'off'
		) {
			const isWarning =
				monthlyConfig.level === 'warning' ||
				monthlyConfig.level === 'holdWarning';
			messages.push({
				type: 'monthlyLimit',
				level: monthlyConfig.level,
				limit: monthlyLimit,
				current: cartQuantity,
				monthQuantity,
			});

			if (isWarning) {
				hasWarning = true;
			} else {
				hasError = true;
			}
		}

		// Check minimum price violation (with contract price bypass)
		const minimumPrice =
			parseFloat(String(this.context.minimumPrices[itemId] || 0)) || 0;

		if (minimumPrice && minimumPrice > 0 && cartPrice < minimumPrice) {
			// Skip if price matches the applicable contract price for this item/quantity
			if (
				isPriceFromContract(
					this.context.contractPriceBreaks,
					itemId,
					cartPrice,
					cartQuantity,
				)
			) {
				// No validation needed - contract price bypass
			} else {
				const priceConfig = this.context.checkConfigs.find(
					(c) => c.type === 'minimumPrice',
				);
				if (priceConfig && priceConfig.level !== 'off') {
					const isWarning =
						priceConfig.level === 'warning' ||
						priceConfig.level === 'holdWarning';
					messages.push({
						type: 'minimumPrice',
						level: priceConfig.level,
						limit: minimumPrice,
						current: cartPrice,
					});

					if (isWarning) {
						hasWarning = true;
					} else {
						hasError = true;
					}
				} else if (!priceConfig) {
					// Default behavior when no config is set - treat as holdWarning (backward compat)
					messages.push({
						type: 'minimumPrice',
						level: 'holdWarning',
						limit: minimumPrice,
						current: cartPrice,
					});
					hasWarning = true;
				}
			}
		}

		// Check price below cost
		if (this.context.priceBelowCostCheck?.enabled && cartPrice > 0) {
			const costField = this.context.priceBelowCostCheck.costField;
			const itemCost = this.context.itemCosts?.[itemId];
			const costValue =
				itemCost != null
					? (itemCost[costField as keyof typeof itemCost] as number | null)
					: null;

			if (costValue != null && costValue > 0 && cartPrice < costValue) {
				// Skip if price matches the applicable contract price for this item/quantity
				const onContractPrice = isPriceFromContract(
					this.context.contractPriceBreaks,
					itemId,
					cartPrice,
					cartQuantity,
				);
				// Skip if the item is on a contract cost: the contract cost is the
				// authoritative acquisition/sell cost, so the item's own stored cost
				// (what costValue is derived from) is stale here and would produce a
				// false below-cost warning. Mirrors the contract-price bypass above.
				// (PD-14364)
				const onContractCost =
					this.context.contractCosts != null &&
					getApplicableContractCost(
						this.context.contractCosts,
						itemId,
						cartQuantity,
					) != null;
				if (onContractPrice || onContractCost) {
					// No validation needed - contract price/cost bypass
				} else {
					const priceBelowCostConfig = this.context.checkConfigs.find(
						(c) => c.type === 'priceBelowCost',
					);
					const level =
						priceBelowCostConfig?.level ?? this.context.priceBelowCostCheck.crm;

					if (level !== 'off') {
						const isWarning = level === 'warning' || level === 'holdWarning';
						messages.push({
							type: 'priceBelowCost',
							level: level as 'warning' | 'holdWarning' | 'error',
							limit: costValue,
							current: cartPrice,
							message: 'Price below cost',
						});

						if (isWarning) {
							hasWarning = true;
						} else {
							hasError = true;
						}
					}
				}
			}
		}

		// Check out of stock
		const stockLevel = this.context.stockLevels[itemId];
		if (stockLevel != null && (cartQuantity > stockLevel || stockLevel <= 0)) {
			const stockConfig = this.context.checkConfigs.find(
				(c) => c.type === 'outOfStock',
			);
			if (stockConfig && stockConfig.level !== 'off') {
				const isWarning =
					stockConfig.level === 'warning' ||
					stockConfig.level === 'holdWarning';
				messages.push({
					type: 'outOfStock',
					level: stockConfig.level,
					limit: stockLevel,
					current: cartQuantity,
					message: 'Insufficient inventory',
				});

				if (isWarning) {
					hasWarning = true;
				} else {
					hasError = true;
				}
			}
		}

		// Check short date — the item is flagged short date (item.isShortDate).
		// Item-intrinsic (no quantity threshold), mirroring how outOfStock above
		// keys off stockLevels. warning/holdWarning -> warning, error -> error.
		if (this.context.shortDates?.[itemId] === true) {
			const shortDateConfig = this.context.checkConfigs.find(
				(c) => c.type === 'shortDate',
			);
			if (shortDateConfig != null && shortDateConfig.level !== 'off') {
				const isWarning =
					shortDateConfig.level === 'warning' ||
					shortDateConfig.level === 'holdWarning';
				messages.push({
					type: 'shortDate',
					level: shortDateConfig.level,
					message: 'Short date item',
				});

				if (isWarning) {
					hasWarning = true;
				} else {
					hasError = true;
				}
			}
		}

		// Check DEA schedule restrictions, address controls, and license validations.
		// effectiveDeaSchedules holds the server-resolved schedule for this item
		// at the selected shipping address (state row > Federal > null).
		const effectiveSchedule = this.context.effectiveDeaSchedules?.[itemId];
		let isBlocking = false;

		// Skip the address-control/license checks entirely when the cart's
		// shipping address can't be resolved: getAddressControls/licenses
		// fail-closed to all-restricted/empty for a missing address, which
		// would wrongly report "RX not allowed" / "license invalid" per item.
		// The single blocking "shipping address not found" message raised in
		// validateOrder covers this case instead.
		if (effectiveSchedule != null && this.context.addressNotFound !== true) {
			// DB may contain mixed-case schedules (e.g. 'Rx') from legacy imports; DTO contract is uppercase.
			const deaType = (effectiveSchedule.schedule ?? 'OTC').toUpperCase();

			// Check address control permissions (BLOCKING)
			if (this.context.addressControls != null) {
				const controls = this.context.addressControls;
				let isAllowed = true;
				let restrictedSchedule: string | null = null;

				if (deaType === 'RX' && !controls.isRxAllowed) {
					isAllowed = false;
					restrictedSchedule = 'RX';
				} else if (deaType === 'C2' && !controls.isSchedule2Allowed) {
					isAllowed = false;
					restrictedSchedule = 'C2';
				} else if (deaType === 'C2N' && !controls.isSchedule2NAllowed) {
					isAllowed = false;
					restrictedSchedule = 'C2N';
				} else if (deaType === 'C3' && !controls.isSchedule3Allowed) {
					isAllowed = false;
					restrictedSchedule = 'C3';
				} else if (deaType === 'C3N' && !controls.isSchedule3NAllowed) {
					isAllowed = false;
					restrictedSchedule = 'C3N';
				} else if (deaType === 'C4' && !controls.isSchedule4Allowed) {
					isAllowed = false;
					restrictedSchedule = 'C4';
				} else if (deaType === 'C5' && !controls.isSchedule5Allowed) {
					isAllowed = false;
					restrictedSchedule = 'C5';
				}

				if (!isAllowed && restrictedSchedule != null) {
					messages.push({
						type: 'itemTypeRestriction',
						level: 'error',
						message: `${restrictedSchedule} items not allowed`,
						data: { deaType, schedule: restrictedSchedule },
						isBlocking: true,
					});
					hasError = true;
					isBlocking = true;
				}
			}

			// Check State Board license validity for RX items (configurable)
			if (deaType === 'RX' && this.context.addressLicenses != null) {
				const stateBoardConfig = this.context.checkConfigs.find(
					(c) => c.type === 'stateBoardLicense',
				);
				// Default to 'error' if no config found (preserves current blocking behavior)
				const stateBoardLevel = stateBoardConfig?.level ?? 'error';

				if (stateBoardLevel !== 'off') {
					const stateBoardLicense = this.context.addressLicenses.find(
						(l) =>
							l.type.toUpperCase().includes('STATE') ||
							l.type.toUpperCase().includes('BOARD'),
					);

					if (!isLicenseValid(stateBoardLicense)) {
						const isWarningLevel =
							stateBoardLevel === 'warning' ||
							stateBoardLevel === 'holdWarning';
						messages.push({
							type: 'stateBoardLicenseInvalid',
							level: stateBoardLevel,
							message: getLicenseFailureMessage(
								'State Board',
								stateBoardLicense,
								this.context.licensesElsewhere?.stateBoard === true,
							),
							data: { deaType, license: stateBoardLicense },
							isBlocking: !isWarningLevel,
						});
						if (isWarningLevel) {
							hasWarning = true;
						} else {
							hasError = true;
							isBlocking = true;
						}
					}
				}
			}

			// Check DEA + State Board license validity for controlled substances (configurable)
			const isControlled = ['C2', 'C2N', 'C3', 'C3N', 'C4', 'C5'].includes(
				deaType,
			);

			if (isControlled && this.context.addressLicenses != null) {
				const deaConfig = this.context.checkConfigs.find(
					(c) => c.type === 'deaLicense',
				);
				const stateBoardConfig = this.context.checkConfigs.find(
					(c) => c.type === 'stateBoardLicense',
				);
				// Default to 'error' if no config found (preserves current blocking behavior)
				const deaLevel = deaConfig?.level ?? 'error';
				const stateBoardLevel = stateBoardConfig?.level ?? 'error';

				if (deaLevel !== 'off') {
					const deaLicense = this.context.addressLicenses.find((l) =>
						l.type.toUpperCase().includes('DEA'),
					);

					if (!isLicenseValid(deaLicense)) {
						const isWarningLevel =
							deaLevel === 'warning' || deaLevel === 'holdWarning';
						messages.push({
							type: 'deaLicenseInvalid',
							level: deaLevel,
							message: getLicenseFailureMessage(
								'DEA',
								deaLicense,
								this.context.licensesElsewhere?.dea === true,
							),
							data: { deaType, deaLicense },
							isBlocking: !isWarningLevel,
						});
						if (isWarningLevel) {
							hasWarning = true;
						} else {
							hasError = true;
							isBlocking = true;
						}
					}
				}

				if (stateBoardLevel !== 'off') {
					const stateBoardLicense = this.context.addressLicenses.find(
						(l) =>
							l.type.toUpperCase().includes('STATE') ||
							l.type.toUpperCase().includes('BOARD'),
					);

					if (!isLicenseValid(stateBoardLicense)) {
						const isWarningLevel =
							stateBoardLevel === 'warning' ||
							stateBoardLevel === 'holdWarning';
						messages.push({
							type: 'stateBoardLicenseInvalid',
							level: stateBoardLevel,
							message: getLicenseFailureMessage(
								'State Board',
								stateBoardLicense,
								this.context.licensesElsewhere?.stateBoard === true,
							),
							data: { deaType, stateBoardLicense },
							isBlocking: !isWarningLevel,
						});
						if (isWarningLevel) {
							hasWarning = true;
						} else {
							hasError = true;
							isBlocking = true;
						}
					}
				}
			}
		}

		const validationState: ValidationState = {
			hasError,
			hasWarning,
			isBlocking,
			messages,
			isOptimistic: cartItem?.isOptimistic || false,
			timestamp: Date.now(),
		};

		this.validationStates.set(itemId, validationState);
		this.notifyListeners(itemId, validationState);
	}

	/**
	 * Clear validation for an item (when quantity becomes 0 or item removed)
	 */
	private clearValidation(itemId: number): void {
		const emptyState: ValidationState = {
			hasError: false,
			hasWarning: false,
			isBlocking: false,
			messages: [],
			isOptimistic: false,
			timestamp: Date.now(),
		};

		this.validationStates.set(itemId, emptyState);
		this.notifyListeners(itemId, emptyState);
	}

	/**
	 * Revalidate all items (when context changes)
	 */
	private revalidateAll(): void {
		Object.keys(this.cartItems).forEach((itemIdStr) => {
			const itemId = parseInt(itemIdStr, 10);
			this.validateItem(itemId);
		});
		this.validateOrder();
	}

	/**
	 * Validate order-level constraints (credit, minimum price, etc.)
	 */
	private validateOrder(): void {
		if (this.context == null) {
			return;
		}

		const messages: Array<ValidationMessage> = [];
		let hasError = false;
		let hasWarning = false;

		// Shipping address not found — the cart references an address that no
		// longer resolves to a live record (e.g. it was retired by the NetSuite
		// address sync). This blocks submission with a clear, address-specific
		// message; the per-item RX/license checks are suppressed for this case
		// (see validateItem) so the rep isn't shown misleading license errors.
		if (this.context.addressNotFound === true) {
			messages.push({
				type: 'shippingAddressNotFound',
				level: 'error',
				message:
					'Shipping address not found — please reselect the shipping address',
				isBlocking: true,
			});
			hasError = true;
		}

		// Credit hold check (use truthy check since value might be 1 or true)
		if (this.context.clientHasCreditHold) {
			const creditHoldConfig = this.context.checkConfigs.find(
				(c) => c.type === 'creditHold',
			);
			const level = creditHoldConfig?.level ?? 'error';

			if (level !== 'off') {
				const isWarning = level === 'warning' || level === 'holdWarning';
				messages.push({
					type: 'creditHold',
					level,
					message: 'Account is on credit hold',
				});

				if (isWarning) {
					hasWarning = true;
				} else {
					hasError = true;
				}
			}
		}

		// Credit limit check

		if (this.context.clientCreditLimit != null && this.orderTotals.total > 0) {
			const creditBalance = this.context.clientCreditBalance ?? 0;
			const projectedBalance = creditBalance + this.orderTotals.total;

			if (projectedBalance > this.context.clientCreditLimit) {
				const creditLimitConfig = this.context.checkConfigs.find(
					(c) => c.type === 'creditLimit',
				);
				const level = creditLimitConfig?.level ?? 'error';

				if (level !== 'off') {
					const isWarning = level === 'warning' || level === 'holdWarning';
					messages.push({
						type: 'creditLimit',
						level,
						limit: this.context.clientCreditLimit,
						current: projectedBalance,
						message: 'Order would exceed credit limit',
						data: {
							limit: this.context.clientCreditLimit,
							balance: creditBalance,
							orderTotal: this.orderTotals.total,
							projected: projectedBalance,
						},
					});

					if (isWarning) {
						hasWarning = true;
					} else {
						hasError = true;
					}
				}
			}
		}

		// Minimum order price check
		if (
			this.context.minimumOrderPrice != null &&
			this.context.minimumOrderPrice > 0
		) {
			if (
				this.orderTotals.subtotal > 0 &&
				this.orderTotals.subtotal < this.context.minimumOrderPrice
			) {
				const minOrderConfig = this.context.checkConfigs.find(
					(c) => c.type === 'minimumOrderPrice',
				);
				const level = minOrderConfig?.level ?? 'error';

				if (level !== 'off') {
					const isWarning = level === 'warning' || level === 'holdWarning';
					messages.push({
						type: 'minimumOrderPrice',
						level,
						limit: this.context.minimumOrderPrice,
						current: this.orderTotals.subtotal,
						message: `Order total below minimum of $${this.context.minimumOrderPrice}`,
						data: {
							minimum: this.context.minimumOrderPrice,
							subtotal: this.orderTotals.subtotal,
						},
					});

					if (isWarning) {
						hasWarning = true;
					} else {
						hasError = true;
					}
				}
			}
		}

		// Maximum discount check
		if (
			this.context.maximumDiscount != null &&
			this.context.maximumDiscount.value > 0 &&
			this.orderTotals.discount > 0
		) {
			let actualDiscount: number;

			if (this.context.maximumDiscount.type === 'percentage') {
				actualDiscount =
					this.orderTotals.subtotal > 0
						? (this.orderTotals.discount / this.orderTotals.subtotal) * 100
						: 0;
			} else {
				actualDiscount = this.orderTotals.discount;
			}

			if (actualDiscount > this.context.maximumDiscount.value) {
				const maxDiscountConfig = this.context.checkConfigs.find(
					(c) => c.type === 'maximumDiscount',
				);
				const level = maxDiscountConfig?.level ?? 'error';

				if (level !== 'off') {
					const isWarning = level === 'warning' || level === 'holdWarning';
					messages.push({
						type: 'maximumDiscount',
						level,
						limit: this.context.maximumDiscount.value,
						current: actualDiscount,
						message: 'Discount exceeds maximum allowed',
						data: {
							discount: actualDiscount,
							maximum: this.context.maximumDiscount.value,
							type: this.context.maximumDiscount.type,
						},
					});

					if (isWarning) {
						hasWarning = true;
					} else {
						hasError = true;
					}
				}
			}
		}

		// Controlled substance percentage check
		// Use CRM-specific fields, falling back to legacy fields for backward compatibility
		const controlCheck = this.context.controlledSubstanceCheck;
		const maxControlPercent =
			controlCheck?.crmMaximumControlPercent ??
			controlCheck?.maximumControlPercent ??
			0;
		const controlUnitType =
			controlCheck?.crmUnitType ?? controlCheck?.unitType ?? 'dollarAmount';

		if (controlCheck?.enabled && maxControlPercent > 0) {
			const restrictedSchedules = ['C2', 'C2N', 'C3', 'C3N', 'C4', 'C5'];
			let totalValue = 0;
			let restrictedValue = 0;

			// Calculate totals based on cart items and their effective DEA schedule
			// (server-resolved against the shipping address state).
			Object.entries(this.cartItems).forEach(([itemIdStr, cartItem]) => {
				const itemId = parseInt(itemIdStr, 10);
				const quantity = cartItem.quantity ?? 0;
				const price = parseFloat(String(cartItem.price ?? 0)) || 0;
				const resolved = this.context?.effectiveDeaSchedules?.[itemId];
				const normalizedSchedule = resolved?.schedule?.toUpperCase() ?? 'OTC';

				if (quantity > 0) {
					if (controlUnitType === 'dollarAmount') {
						const lineTotal = quantity * price;
						totalValue += lineTotal;

						if (restrictedSchedules.includes(normalizedSchedule)) {
							restrictedValue += lineTotal;
						}
					} else if (controlUnitType === 'sizeCountQuantity') {
						// UOM Quantity: quantity * sizeCount
						const sizeCount = this.context?.itemSizeCounts?.[itemId] ?? 1;
						const uomQty = quantity * sizeCount;
						totalValue += uomQty;

						if (restrictedSchedules.includes(normalizedSchedule)) {
							restrictedValue += uomQty;
						}
					} else {
						// unitsPurchased: raw quantity
						totalValue += quantity;

						if (restrictedSchedules.includes(normalizedSchedule)) {
							restrictedValue += quantity;
						}
					}
				}
			});

			if (totalValue > 0) {
				const actualPercentage = (restrictedValue / totalValue) * 100;

				if (actualPercentage > maxControlPercent) {
					const level = controlCheck.crm;

					if (level !== 'off') {
						const isWarning = level === 'warning' || level === 'holdWarning';
						messages.push({
							type: 'controlledSubstancePercentage',
							level,
							limit: maxControlPercent,
							current: actualPercentage,
							message: `Controlled substance percentage (${actualPercentage.toFixed(1)}%) exceeds maximum (${maxControlPercent}%)`,
							data: {
								actualPercentage,
								maxPercentage: maxControlPercent,
								restrictedValue,
								totalValue,
								unitType: controlUnitType,
							},
						});

						if (isWarning) {
							hasWarning = true;
						} else {
							hasError = true;
						}
					}
				}
			}
		}

		this.orderValidationState = {
			hasError,
			hasWarning,
			messages,
			timestamp: Date.now(),
		};

		this.notifyOrderListeners();
	}

	/**
	 * Subscribe to order-level validation state changes
	 */
	subscribeToOrder(listener: OrderValidationListener): () => void {
		this.orderListeners.add(listener);

		// Immediately call listener with current state
		if (this.orderValidationState != null) {
			listener(this.orderValidationState);
		}

		return () => {
			this.orderListeners.delete(listener);
		};
	}

	/**
	 * Get current order-level validation state
	 */
	getOrderValidationState(): OrderValidationState | null {
		return this.orderValidationState;
	}

	/**
	 * Notify all listeners for an item
	 */
	private notifyListeners(
		itemId: number,
		validationState: ValidationState,
	): void {
		const itemListeners = this.listeners.get(itemId);

		if (itemListeners != null) {
			itemListeners.forEach((listener) => {
				try {
					listener(itemId, validationState);
				} catch (error) {
					// Error: CartValidationManager Error in validation listener
				}
			});
		}
	}

	/**
	 * Notify all order-level listeners
	 */
	private notifyOrderListeners(): void {
		if (this.orderValidationState == null) {
			return;
		}

		this.orderListeners.forEach((listener) => {
			try {
				listener(this.orderValidationState!);
			} catch (error) {
				// Error: CartValidationManager Error in order validation listener
			}
		});
	}

	/**
	 * Clean up resources
	 */
	public cleanup(): void {
		this.listeners.clear();
		this.orderListeners.clear();
		this.validationStates.clear();
		this.orderValidationState = null;
		this.cartItems = {};
		this.context = null;
		this.orderTotals = { subtotal: 0, discount: 0, total: 0 };
	}
}

// Export singleton instance
export const CartValidationManager = new CartValidationManagerClass();
export type {
	AddressControls,
	AddressLicense,
	ContractPriceBreak,
	ControlledSubstanceCheck,
	OrderValidationState,
	ValidationContext,
	ValidationMessage,
	ValidationState,
};
